import React, { useEffect, useState } from "react";
import axios from "axios";
import { analyzeCustomerContactSource, sourceKey } from "./CustomerContactRepairSourceEvidence";
import { selectAllReadyContactRepairKeys } from "./contactRepairReadySelection";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, Typography, Table, TableHead, TableBody, TableCell, TableRow,
  TextField, MenuItem, FormControlLabel,
} from "@mui/material";

const getError = (error) => error?.response?.data?.error || "Unable to verify contact repair. No changes were made.";

export default function CustomerContactRepairDialog({ open, onClose, customerId, onRepaired }) {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);
  const [unmatchedLimit, setUnmatchedLimit] = useState(30);
  const [suggestionLimit, setSuggestionLimit] = useState(20);
  const [manualNameId, setManualNameId] = useState("");
  const [manualEmailId, setManualEmailId] = useState("");
  const [manualPairs, setManualPairs] = useState([]);
  const [manualPreviewing, setManualPreviewing] = useState(false);
  const [manualConfirmedEmail, setManualConfirmedEmail] = useState("");
  const [sourceEvidence, setSourceEvidence] = useState(null);
  const [sourceFileName, setSourceFileName] = useState("");
  const [sourceEvidenceLimit, setSourceEvidenceLimit] = useState(25);
  const [sourceRecovery, setSourceRecovery] = useState(null);
  const [sourceConfirmEmail, setSourceConfirmEmail] = useState("");
  const [sourceApplying, setSourceApplying] = useState(false);
  const endpoint = `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/contact-repair/${customerId}`;
  const config = () => ({ headers: { accessToken: localStorage.getItem("accessToken") } });

  useEffect(() => {
    if (!open || !customerId) return undefined;
    let cancelled = false;
    setPreview(null);
    setSelected([]);
    setReviewAcknowledged(false);
    setManualPairs([]);
    setManualNameId("");
    setManualEmailId("");
    setManualConfirmedEmail("");
    setSourceEvidence(null);
    setSourceFileName("");
    setSourceRecovery(null);
    setSourceConfirmEmail("");
    setError("");
    setLoading(true);
    axios.get(`${endpoint}/preview`, config())
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch((err) => { if (!cancelled) setError(getError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, customerId, endpoint]);

  const canSelect = (pair) => Boolean(preview?.writesEnabled && pair.safe &&
    (pair.confidence === "strong" || pair.confidence === "review"));
  // Select across the full preview (not only the first 20 visible cards).
  const allReadyKeys = selectAllReadyContactRepairKeys(preview?.proposals || []);
  const reviewedKeys = (preview?.proposals || [])
    .filter((pair) => selected.includes(pair.key) && pair.confidence === "review")
    .map((pair) => pair.key);
  const selectedManualPairs = manualPairs.filter((pair) =>
    selected.includes(`manual::${pair.keepId}::${pair.removeId}`));
  const selectedManualEmail = (preview?.proposals || [])
    .find((pair) => pair.manual && selected.includes(pair.key))?.remove.email || "";
  const manualEmailVerified = !selectedManualEmail ||
    manualConfirmedEmail.trim().toLowerCase() === selectedManualEmail.toLowerCase();
  const visibleProposals = (preview?.proposals || []).filter((pair) => {
    if (view === "unmatched") return false;
    if (view === "ready" && !(pair.safe && pair.confidence === "strong")) return false;
    if (view === "review" && !(pair.safe && pair.confidence === "review")) return false;
    if (view === "blocked" && pair.safe) return false;
    const tokens = [pair.match, pair.keep.firstName, pair.keep.lastName,
      pair.keep.email, pair.remove.firstName, pair.remove.lastName, pair.remove.email];
    return tokens.join(" ").toLowerCase().includes(search.trim().toLowerCase());
  });
  const visibleUnmatched = (preview?.unmatchedContacts || []).filter((contact) =>
    [contact.firstName, contact.lastName, contact.email, contact.phone, contact.kind]
      .join(" ").toLowerCase().includes(search.trim().toLowerCase()));
  const conflictingSelections = (keys) => {
    const rows = (preview?.proposals || []).filter((pair) => keys.includes(pair.key));
    const used = new Set();
    return rows.some((row) => {
      if (used.has(row.keepId) || used.has(row.removeId)) return true;
      used.add(row.keepId);
      used.add(row.removeId);
      return false;
    });
  };
  const selectAllReady = () => {
    if (!preview?.writesEnabled || loading || applying) return;
    setError("");
    setReviewAcknowledged(false);
    setManualConfirmedEmail("");
    setSelected(allReadyKeys);
  };
  const toggle = (key, checked) => {
    const keys = checked ? [...selected, key] : selected.filter((value) => value !== key);
    if (conflictingSelections(keys)) {
      setError("These suggestions share a contact. Select one pairing at a time.");
      return;
    }
    setError("");
    setReviewAcknowledged(false);
    setManualConfirmedEmail("");
    setSelected(keys);
  };
  const refresh = async () => {
    setLoading(true);
    setError("");
    // A successful repair or another user's edit invalidates the old source
    // comparison; require the workbook to be explicitly rechecked.
    setSourceEvidence(null);
    setSourceFileName("");
    setSourceRecovery(null);
    setSourceConfirmEmail("");
    setSelected([]);
    setReviewAcknowledged(false);
    setManualPairs([]);
    setManualNameId("");
    setManualEmailId("");
    setManualConfirmedEmail("");
    try {
      const { data } = await axios.get(`${endpoint}/preview`, config());
      setPreview(data);
    } catch (err) { setPreview(null); setError(getError(err)); }
    finally { setLoading(false); }
  };
  const previewManualPair = async () => {
    if (!manualNameId || !manualEmailId) return;
    const pair = { keepId: manualNameId, removeId: manualEmailId };
    setManualPreviewing(true);
    setError("");
    try {
      const { data } = await axios.post(`${endpoint}/manual-preview`,
        { manualPairs: [pair] }, config());
      setPreview(data);
      setManualPairs([pair]);
      setSelected([]);
      setReviewAcknowledged(false);
      setManualConfirmedEmail("");
      const proposed = data.proposals?.find((item) => item.manual);
      setView(proposed?.safe ? "review" : "blocked");
      setSearch("Administrator-selected unmatched pair");
      setManualNameId("");
      setManualEmailId("");
      setSuggestionLimit(20);
    } catch (err) {
      setError(getError(err));
    } finally {
      setManualPreviewing(false);
    }
  };

  const inspectSourceWorkbook = async (file) => {
    if (!file || !preview) return;
    setError("");
    setSourceEvidence(null);
    setSourceFileName("");
    setSourceRecovery(null);
    setSourceConfirmEmail("");
    try {
      if (file.size > 8 * 1024 * 1024) {
        throw new Error("The import workbook is too large for a local source comparison.");
      }
      // Load the spreadsheet parser only when an Administrator chooses a file.
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets["Your Clients"];
      if (!sheet) throw new Error("Expected a 'Your Clients' sheet from the Elite Core import template.");
      const rows = XLSX.utils.sheet_to_json(sheet, { range: 1, defval: "" });
      const customerRows = rows.filter((row) =>
        sourceKey(row["Client Name *"]) === sourceKey(preview.customerName) &&
        String(row["Row Type *"] || "").trim().toUpperCase() === "CONTACT");
      if (!customerRows.length) {
        throw new Error("No CONTACT rows belong to this exact customer in the selected workbook.");
      }
      const result = analyzeCustomerContactSource(preview.contacts || [], customerRows);
      setSourceEvidence(result);
      setSourceFileName(file.name);
      setSourceEvidenceLimit(25);
    } catch (err) {
      setError(err.message || "Unable to analyze this import workbook.");
    }
  };

  const recoverSourceEmail = async () => {
    if (!sourceRecovery || !preview || sourceApplying ||
        sourceConfirmEmail.trim().toLowerCase() !== sourceRecovery.email ||
        sourceRecovery.state !== "email-not-in-core" || sourceRecovery.usageCount) {
      return;
    }
    if (!window.confirm(`Restore ${sourceRecovery.email} to ${sourceRecovery.name}?
This changes only the saved email field on one contact, not any order documents.
The change will be audited and can only be undone if no subsequent references exist.`)) return;
    setSourceApplying(true);
    setError("");
    try {
      await axios.post(`${endpoint}/source-email`, {
        fingerprint: preview.fingerprint,
        contactId: sourceRecovery.contactId,
        sourceFirstName: sourceRecovery.firstName,
        sourceLastName: sourceRecovery.lastName,
        sourceEmail: sourceRecovery.email,
        confirmedEmail: sourceConfirmEmail,
      }, config());
      await refresh();
      onRepaired?.();
    } catch (err) {
      setError(getError(err));
    } finally {
      setSourceApplying(false);
    }
  };

  const apply = async () => {
    if (!manualEmailVerified) {
      setError("Re-enter the selected email address exactly before confirming a manual match.");
      return;
    }
    if (reviewedKeys.length && !reviewAcknowledged) {
      setError("Confirm that you have manually verified each ambiguous email and identity.");
      return;
    }
    if (!selected.length || !window.confirm(`Repair ${selected.length} selected contact pair(s)? ${reviewedKeys.length} need your verified identity decision. Original contact IDs will be retained as inactive historical aliases.`)) return;
    setApplying(true);
    setError("");
    try {
      await axios.post(`${endpoint}/apply`, {
        fingerprint: preview.fingerprint, keys: selected, reviewedKeys,
        manualPairs: selectedManualPairs, manualConfirmedEmail,
      }, config());
      await refresh();
      onRepaired?.();
    } catch (err) {
      setSelected([]);
      setError(getError(err));
    } finally { setApplying(false); }
  };
  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Contact Repair — {preview?.customerName || "Customer"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">Read-only impact preview. Every suggested contact is checked for project, Sales Order, invoice, purchase order, PromoStore and portal references. Linked or uncertain records cannot be merged here. Existing document snapshots are never changed.</Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {loading && <Typography>Checking contacts and historical document references...</Typography>}
          {preview && <>
            {!preview.writesEnabled && <Alert severity="warning">Contact Repair is currently read-only. Applying changes is disabled until the database migration, sandbox testing and release approval are complete.</Alert>}
            <Typography variant="body2">
              {preview.summary.active} active contacts · {preview.summary.total - preview.summary.active} retained historical aliases
              {" · "}{preview.summary.suggestions} suggestions
              {" · "}{preview.summary.eligibleStrong} ready
              {" · "}{preview.summary.needsReview} review choices
              {" · "}{preview.summary.blocked} blocked
              {" · "}{preview.summary.unmatched} unmatched
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Checked {preview.summary.linkedProjects} projects, {preview.summary.linkedSalesOrders} Sales Orders,
              {" "}{preview.summary.linkedInvoices} invoices, {preview.summary.linkedPurchaseOrders} purchase orders,
              {" "}{preview.summary.linkedPromoStores || 0} PromoStores, and
              {" "}{preview.summary.linkedPromoStoreOrders || 0} PromoStore orders,
              {" "}{preview.summary.linkedCartSnapshots || 0} frozen cart snapshots,
              and {" "}{preview.summary.linkedProjectReferences || 0} project references.
              Strong suggestions still require explicit selection.
            </Typography>
            {preview.summary.sharedFallbackNumbers > 0 && (
              <Alert severity="warning">
                Detected {preview.summary.sharedFallbackNumbers} repeated phone number(s) on email-only imports.
                These are treated as shared company fallbacks, never copied into someone's personal phone field.
                The original number remains on the historical contact record for audit.
              </Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField select size="small" label="Show" value={view}
                onChange={(event) => { setView(event.target.value); setUnmatchedLimit(30); setSuggestionLimit(20); }}
                sx={{ minWidth: 215 }}>
                <MenuItem value="all">All suggested matches</MenuItem>
                <MenuItem value="ready">Ready ({preview.summary.eligibleStrong})</MenuItem>
                <MenuItem value="review">Needs verification ({preview.summary.needsReview})</MenuItem>
                <MenuItem value="blocked">Blocked ({preview.summary.blocked})</MenuItem>
                <MenuItem value="unmatched">Unmatched ({preview.summary.unmatched})</MenuItem>
              </TextField>
              <TextField size="small" fullWidth label="Search contacts and emails"
                value={search} onChange={(event) => { setSearch(event.target.value); setUnmatchedLimit(30); setSuggestionLimit(20); }}/>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Button size="small" variant="outlined"
                disabled={loading || applying || !preview.writesEnabled || allReadyKeys.length === 0}
                onClick={selectAllReady}>
                Select all ready ({allReadyKeys.length})
              </Button>
              <Button size="small" disabled={loading || applying || !selected.length}
                onClick={() => {
                  setSelected([]);
                  setReviewAcknowledged(false);
                  setManualConfirmedEmail("");
                  setError("");
                }}>
                Clear selection
              </Button>
              <Typography variant="caption" color="text.secondary">
                {selected.length} selected · across all pages. Blocked and identity-review matches are excluded.
              </Typography>
            </Stack>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Compare against original customer import</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select the original Elite Core Client Import workbook. This checks
                  exact imported names against currently missing emails and detects
                  emails already stored on another contact. The spreadsheet stays in
                  your browser; this is evidence only and never changes customer data.
                </Typography>
                <Button size="small" variant="outlined" component="label" sx={{ alignSelf: "flex-start" }}>
                  Choose Excel import workbook
                  <input type="file" hidden accept=".xlsx,.xls"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) inspectSourceWorkbook(file);
                      event.target.value = "";
                    }} />
                </Button>
                {sourceEvidence && (
                  <>
                    <Typography variant="body2">
                      <strong>{sourceFileName}</strong> · {sourceEvidence.stats.nameOnlyChecked} name-only contacts checked
                      {" · "}{sourceEvidence.stats.exactSourceEmail} exact source email matches
                      {" · "}{sourceEvidence.stats.emailAlreadyInCore} already in Core
                      {" · "}{sourceEvidence.stats.emailNotInCore} missing from Core
                    </Typography>
                    <Alert severity="info">
                      These are source-document matches, not automatic merge approvals.
                      An existing email owner may be a different person or a shared mailbox.
                      Use Contact Repair's separate impact-checked proposal before merging,
                      and review missing emails in the standard Contacts editor.
                      Do not overwrite a field or bypass a saved Project/Sales Order link.
                    </Alert>
                    {sourceEvidence.stats.misplacedTitleEmail > 0 && (
                      <Alert severity="warning">
                        {sourceEvidence.stats.misplacedTitleEmail} contact(s) have what looks
                        like an email address stored in their job-title field. Review those
                        separately; job titles are not modified here.
                      </Alert>
                    )}
                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small" aria-label="Original import email evidence">
                        <TableHead><TableRow>
                          <TableCell>Existing name-only contact</TableCell>
                          <TableCell>Source email</TableCell>
                          <TableCell>Evidence / next step</TableCell>
                          <TableCell>Review missing email</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {sourceEvidence.candidates.slice(0, sourceEvidenceLimit).map((item) => (
                            <TableRow key={item.contactId}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell sx={{ overflowWrap: "anywhere" }}>
                                {item.email || item.sourceEmails.join(", ") || item.titleEmail || "—"}
                              </TableCell>
                              <TableCell>
                                {item.usageCount > 0 && (
                                  <Typography variant="caption" color="error">
                                    {item.usageCount} existing reference(s) — blocked.
                                  </Typography>
                                )}
                                {item.state === "email-already-in-core" ?
                                  `Email exists on ${item.ownerCount} other Core contact(s); verify identity before merging` :
                                item.state === "email-not-in-core" ?
                                  "Email exists only in the source workbook; manually verify before filling" :
                                item.state === "review-source-identity" ?
                                  "Likely department or import identifier; verify source record" :
                                item.state === "source-conflict" ?
                                  "Several source emails for this name; resolve manually" :
                                item.state === "source-shared-email" ?
                                  "The same source email is assigned to multiple names; do not auto-fill" :
                                  item.titleEmail ? "Email found in title field; verify separately" :
                                  "No exact source email; leave unchanged"}
                              </TableCell>
                              <TableCell>
                                {item.state === "email-not-in-core" && (
                                  <Button size="small" variant="text"
                                    disabled={item.usageCount > 0 || !preview.writesEnabled || sourceApplying}
                                    onClick={() => {
                                      setSourceRecovery(item);
                                      setSourceConfirmEmail("");
                                    }}>
                                    Preview recovery
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    {sourceRecovery && (
                      <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle2">Recover one source email — before and after</Typography>
                          <Typography variant="body2">
                            <strong>{sourceRecovery.name}</strong> (saved UUID: {sourceRecovery.contactId})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Before:</strong> Email missing
                          </Typography>
                          <Typography variant="body2">
                            <strong>After:</strong> {sourceRecovery.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Existing phone, name, title, primary status and contact UUID stay unchanged.
                            The server will recheck all historical document links, email duplicates,
                            customer identity and preview freshness before saving.
                          </Typography>
                          <TextField size="small" label="Re-enter the exact source email"
                            value={sourceConfirmEmail}
                            onChange={(event) => setSourceConfirmEmail(event.target.value)}
                            helperText="Required to verify this particular email before saving." />
                          <Stack direction="row" spacing={1}>
                            <Button size="small" onClick={() => {
                              setSourceRecovery(null);
                              setSourceConfirmEmail("");
                            }}>Cancel</Button>
                            <Button size="small" variant="contained"
                              disabled={sourceApplying || sourceRecovery.usageCount > 0 ||
                                !preview.writesEnabled ||
                                sourceConfirmEmail.trim().toLowerCase() !== sourceRecovery.email}
                              onClick={recoverSourceEmail}>
                              {sourceApplying ? "Saving..." : "Confirm one email recovery"}
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                    {sourceEvidence.candidates.length > sourceEvidenceLimit && (
                      <Button size="small" onClick={() =>
                        setSourceEvidenceLimit((limit) => limit + 25)}>
                        Show more source comparisons
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Box>
            <Divider />
            {(preview.proposals || []).length === 0 &&
              <Alert severity="info">No suggested pairs were found. Review unmatched contacts separately.</Alert>}
            {view !== "unmatched" && visibleProposals.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No suggestions match this filter. Try a different category or search.
              </Typography>
            )}
            {view === "unmatched" && (
              <Stack spacing={1}>
                <Alert severity="info">
                  These contacts have no defensible matching record. An email-only entry may
                  be a shared mailbox, and an imported name may be an instruction or department,
                  not a person. Contact Repair will not fabricate identities or addresses.
                </Alert>
                <Typography variant="body2">
                  {visibleUnmatched.length} unmatched contacts (showing up to {Math.min(unmatchedLimit, visibleUnmatched.length)})
                </Typography>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">Preview a manually verified pairing</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose one unmatched name and one unmatched email. The server
                      will recheck both contacts, all saved document references and
                      any conflicting fields. This does not save or merge anything.
                    </Typography>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <TextField select size="small" label="Name-only contact"
                        value={manualNameId} onChange={(event) => setManualNameId(event.target.value)}
                        sx={{ flex: 1 }}>
                        <MenuItem value="">Select a person</MenuItem>
                        {(preview.unmatchedContacts || []).filter((contact) =>
                          contact.kind === "name-only").map((contact) => (
                          <MenuItem key={contact.contactId} value={contact.contactId}>
                            {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField select size="small" label="Email-only contact"
                        value={manualEmailId} onChange={(event) => setManualEmailId(event.target.value)}
                        sx={{ flex: 1 }}>
                        <MenuItem value="">Select an email address</MenuItem>
                        {(preview.unmatchedContacts || []).filter((contact) =>
                          contact.kind === "email-only").map((contact) => (
                          <MenuItem key={contact.contactId} value={contact.contactId}>
                            {contact.email}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Button variant="outlined" disabled={!manualNameId || !manualEmailId ||
                      loading || applying || manualPreviewing}
                      onClick={previewManualPair}>
                      {manualPreviewing ? "Verifying..." : "Preview this manual pairing"}
                    </Button>
                  </Stack>
                </Box>
                {visibleUnmatched.slice(0, unmatchedLimit).map((contact) => (
                  <Box key={contact.contactId} sx={{ px: 2, py: 1, border: "1px solid",
                    borderColor: "divider", borderRadius: 1 }}>
                    <Typography variant="body2">
                      {[contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
                        "(Name missing)"} · {contact.email || "(Email missing)"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {contact.kind} · ID {contact.contactId}
                    </Typography>
                  </Box>
                ))}
                {visibleUnmatched.length > unmatchedLimit && (
                  <Button size="small" onClick={() => setUnmatchedLimit((limit) => limit + 30)}>
                    Show more unmatched contacts
                  </Button>
                )}
              </Stack>
            )}
            {view !== "unmatched" && visibleProposals.slice(0, suggestionLimit).map((pair) => (
              <Box key={pair.key} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox checked={selected.includes(pair.key)} disabled={!canSelect(pair) || applying}
                      onChange={(event) => toggle(pair.key, event.target.checked)} />
                    <Typography fontWeight={600}>{pair.match}</Typography>
                  </Stack>
                  <Chip size="small" color={pair.blockedReasons.length ? "error" : pair.confidence === "review" ? "warning" : "success"}
                    label={pair.blockedReasons.length ? "Blocked" : pair.confidence === "review" ? "Verify identity" : "Ready"} />
                </Stack>
                <Stack spacing={1} sx={{ ml: 5 }}>
                  <Typography variant="body2"><strong>Keep:</strong> {[pair.keep.firstName, pair.keep.lastName].filter(Boolean).join(" ") || "(name missing)"} · {pair.keep.email || "(email missing)"}</Typography>
                  <Typography variant="body2"><strong>Other record:</strong> {[pair.remove.firstName, pair.remove.lastName].filter(Boolean).join(" ") || "(name missing)"} · {pair.remove.email || "(email missing)"}</Typography>
                  <Typography variant="body2"><strong>Preview result:</strong> {[pair.result.firstName, pair.result.lastName].filter(Boolean).join(" ")} · {pair.result.email}</Typography>
                  {pair.evidence && <Typography variant="caption" color="warning.main">{pair.evidence}</Typography>}
                  {pair.ignoredSharedPhone && (
                    <Alert severity="info" sx={{ py: 0 }}>
                      The imported phone {pair.ignoredSharedPhone.value} appears on
                      {" "}{pair.ignoredSharedPhone.repeatedOn} email-only contacts.
                      It will not overwrite or fill this person's phone.
                    </Alert>
                  )}
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" aria-label="Contact repair field changes">
                      <TableHead><TableRow>
                        <TableCell>Field</TableCell><TableCell>Keep</TableCell><TableCell>Other</TableCell><TableCell>After repair</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {["phone", "mobile", "title", "department", "note", "isPrimary", "contactId"].map((field) => (
                          <TableRow key={field}>
                            <TableCell>{field === "contactId" ? "Contact UUID" : field}</TableCell>
                            {[pair.keep, pair.remove, pair.result].map((contact, column) => (
                              <TableCell key={column} sx={{ overflowWrap: "anywhere" }}>
                                {field === "isPrimary" ? (contact[field] ? "Yes" : "No") : contact[field] || "(empty)"}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  {(pair.keepUsage.count > 0 || pair.removeUsage.count > 0) &&
                    <Typography variant="caption" color="error">
                      Keep record: {pair.keepUsage.count} references; other record: {pair.removeUsage.count} references.
                    </Typography>}
                  {[...(pair.keepUsage.examples || []), ...(pair.removeUsage.examples || [])].map((usage, index) =>
                    <Typography key={index} variant="caption" color="text.secondary">
                      {usage.type} #{usage.recordId} · {usage.field}
                    </Typography>)}
                  {pair.blockedReasons.map((reason) =>
                    <Typography key={reason} color="error" variant="caption">{reason}</Typography>)}
                  {pair.confidence === "review" && !pair.blockedReasons.length &&
                    <Typography variant="caption" color="warning.main">
                      Review candidate: verify the person's identity and correct email address.
                      If confirmed, you may select this pair with explicit acknowledgement.
                    </Typography>}
                </Stack>
              </Box>
            ))}
            {view !== "unmatched" && visibleProposals.length > suggestionLimit && (
              <Button size="small" onClick={() => setSuggestionLimit((limit) => limit + 20)}>
                Show more suggested matches
              </Button>
            )}
          </>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {selectedManualEmail && (
          <TextField size="small" label="Re-enter email to confirm manual match"
            value={manualConfirmedEmail} autoComplete="off"
            onChange={(event) => setManualConfirmedEmail(event.target.value)}
            helperText="No guesswork: type the email shown in the before/after preview."
            sx={{ minWidth: 280 }} />
        )}
        {reviewedKeys.length > 0 && (
          <FormControlLabel
            control={<Checkbox checked={reviewAcknowledged}
              onChange={(event) => setReviewAcknowledged(event.target.checked)} />}
            label={`I have verified the identity and email for ${reviewedKeys.length} review choice(s).`}
          />
        )}
        <Button onClick={onClose} disabled={applying}>Close</Button>
        <Button onClick={refresh} disabled={loading || applying}>Recheck impacts</Button>
        <Button variant="contained" disabled={loading || applying || !selected.length || !preview?.writesEnabled ||
          (reviewedKeys.length > 0 && !reviewAcknowledged) || !manualEmailVerified}
          onClick={apply}>{applying ? "Repairing..." : `Repair ${selected.length} selected`}</Button>
      </DialogActions>
    </Dialog>
  );
}
